import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from 'rsuite';
import type { FaixaDePreco } from '../../../@types/IPA/PrecoPsicologico';
import { useCriarFaixa, useEditarFaixa, useListarFaixas } from '../../../hooks/usePrecoPsicologico';
import { usePrecoPsicologicoStore } from '../../../store/usePrecoPsicologicoStore';
import { FaixaDePrecoForm } from './components/FaixaDePrecoForm';
import type { FaixaDePrecoFormData } from './components/FaixaDePrecoForm/schema';
import { FaixaDePrecoList } from './components/FaixaDePrecoList';
import { ModalConfirmarExclusao } from './components/ModalConfirmarExclusao';
import { PrecoPsicologicoHeader } from './components/PrecoPsicologicoHeader';
import emptyStateImg from './assets/isa-empty-state.png';
import './styles/PrecoPsicologico.scss';

interface ErroApi {
  response?: {
    data?: {
      _embedded?: { errors?: Array<{ message?: string }> };
      message?: string;
      erro?: string;
    };
  };
}

function extrairMensagemErro(err: unknown): string {
  const data = (err as ErroApi)?.response?.data;
  return (
    data?._embedded?.errors?.[0]?.message ??
    data?.message ??
    data?.erro ??
    ''
  );
}

function isErroSobreposicao(err: unknown): boolean {
  const msg = extrairMensagemErro(err).toLowerCase();
  return msg.includes('sobrepoem') || msg.includes('sobrepõe') || msg.includes('sobreposta') || msg.includes('overlap');
}

export default function PrecoPsicologicoPage() {
  const [faixaSelecionada, setFaixaSelecionada] = useState<FaixaDePreco | undefined>();
  const [mostrarForm, setMostrarForm] = useState(false);
  const [erroSobreposicao, setErroSobreposicao] = useState(false);

  const { abrirModalExclusao } = usePrecoPsicologicoStore();
  const { data: faixas = [], isLoading } = useListarFaixas();
  const { mutate: criar, isLoading: criando } = useCriarFaixa();
  const { mutate: editar, isLoading: editando } = useEditarFaixa();

  useEffect(() => {
    if (!isLoading && faixas.length === 0) {
      setMostrarForm(true);
    }
  }, [isLoading, faixas.length]);

  const ultimaAtualizacao = useMemo(() => {
    if (faixas.length === 0) return undefined;
    return faixas
      .map(f => f.atualizadoEm ?? f.criadoEm)
      .filter(Boolean)
      .sort()
      .at(-1);
  }, [faixas]);

  function handleSelecionar(faixa: FaixaDePreco) {
    setFaixaSelecionada(faixa);
    setMostrarForm(true);
    setErroSobreposicao(false);
  }

  function handleAdicionarNova() {
    setFaixaSelecionada(undefined);
    setMostrarForm(true);
    setErroSobreposicao(false);
  }

  function handleCancelar() {
    setMostrarForm(false);
    setFaixaSelecionada(undefined);
    setErroSobreposicao(false);
  }

  function handleSalvar(data: FaixaDePrecoFormData) {
    setErroSobreposicao(false);

    const idEdicao = faixaSelecionada?.id;
    const mensagemFallback = idEdicao
      ? 'Erro ao atualizar faixa. Tente novamente.'
      : 'Erro ao criar faixa. Tente novamente.';

    const onSuccess = () => {
      setMostrarForm(false);
      setFaixaSelecionada(undefined);
    };

    const onError = (err: unknown) => {
      const mensagem = extrairMensagemErro(err);
      if (isErroSobreposicao(err)) {
        setErroSobreposicao(true);
        Alert.error(mensagem || 'Não foi possível salvar porque já existe regra para essa faixa.', 7000);
        return;
      }
      Alert.error(mensagem || mensagemFallback, 5000);
    };

    if (idEdicao) {
      editar({ id: idEdicao, data }, { onSuccess, onError });
      return;
    }
    criar(data, { onSuccess, onError });
  }

  function handleExcluirDaLista(faixa: FaixaDePreco) {
    abrirModalExclusao(faixa);
  }

  function handleExcluirDoForm() {
    if (faixaSelecionada) {
      abrirModalExclusao(faixaSelecionada);
    }
  }

  if (isLoading) {
    return <div className="pp-loading">Carregando...</div>;
  }

  return (
    <div className="pp-page">
      <PrecoPsicologicoHeader faixas={faixas} />

      <div className="pp-page__conteudo">
        <FaixaDePrecoList
          faixas={faixas}
          idSelecionado={faixaSelecionada?.id}
          onSelecionar={handleSelecionar}
          onExcluir={handleExcluirDaLista}
          onAdicionarNova={handleAdicionarNova}
        />

        <div className="pp-page__area-form">
          {mostrarForm ? (
            <FaixaDePrecoForm
              key={faixaSelecionada?.id ?? 'nova'}
              faixaEmEdicao={faixaSelecionada}
              onSave={handleSalvar}
              onCancel={handleCancelar}
              onDelete={faixaSelecionada?.id ? handleExcluirDoForm : undefined}
              erroSobreposicao={erroSobreposicao}
              salvando={criando || editando}
            />
          ) : (
            <div className="pp-page__vazio-card">
              <div className="pp-page__vazio-inner">
                <img className="pp-page__vazio-img" src={emptyStateImg} alt="" />
                <p className="pp-page__vazio-texto">
                  {faixas.length === 0
                    ? 'Crie a primeira faixa de preço psicológico'
                    : 'Selecione uma faixa ao lado ou adicione uma nova'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <ModalConfirmarExclusao />
    </div>
  );
}
